import { isPlatformServer, ViewportScroller } from '@angular/common';
import { EnvironmentProviders, makeEnvironmentProviders, APP_INITIALIZER, PLATFORM_ID } from '@angular/core';
import { Router, Scroll, NavigationStart, NavigationSkipped } from '@angular/router';
import { filter, pairwise } from 'rxjs/operators';

import { LogService } from '@usheru-hff/shared/utils';

const SCROLL_DEBUG_PREFIX = '[scroll-reposition]';
const SCROLL_RESTORE_MAX_RETRIES = 10;
const SCROLL_RESTORE_RETRY_DELAY_MS = 250;

function getPath(url: string): string {
  return url.split('?')[0];
}

function getPageParam(url: string): string | null {
  const query = url.split('?')[1];
  if (!query) {
    return null;
  }

  return new URLSearchParams(query).get('page');
}

function getMaxScrollableY(): number {
  const doc = document.documentElement;
  return Math.max(0, doc.scrollHeight - window.innerHeight);
}

/**
 * Use only `provideScrollRestoration` only if  `scrollPositionRestoration` is disabled and
 *  outside the `provideRouter`.
 *
 * This is mostly used to restore the scroll without updating it when the params of a URL has changed, like on Catalogue pages.
 *
 * Example: Panoptico `app.config.ts`
 *
 * provideRouter(
 *    ...,
 *    withInMemoryScrolling({
 *      anchorScrolling: 'enabled',
 *      scrollPositionRestoration: 'disabled'
 *    }),
 *  ),
 * provideScrollRestoration()
 * */
export function provideScrollRestoration(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: APP_INITIALIZER,
      useFactory: (router: Router, viewportScroller: ViewportScroller, platformId, logService: LogService) => () => {
        // console.info('✅ Scroll Restoration Provider Initialized (APP_INITIALIZER)');
        const logger = logService.scoped('scroll-reposition');

        let lastScrollPosition = 0;
        let lastNavigationUrl = router.url;
        let shouldRestoreCatalogBackScroll = false;
        let hasRestoredForCurrentNavigation = false;
        let restoreTimer: ReturnType<typeof setTimeout> | null = null;

        const clearRestoreTimer = () => {
          if (restoreTimer !== null) {
            clearTimeout(restoreTimer);
            restoreTimer = null;
          }
        };

        const clearRestorationState = (reason: string) => {
          lastScrollPosition = 0;
          shouldRestoreCatalogBackScroll = false;
          hasRestoredForCurrentNavigation = true;

          logger.log('clearRestorationState', `${SCROLL_DEBUG_PREFIX} Restoration state cleared`, {
            reason,
            lastScrollPosition,
            shouldRestoreCatalogBackScroll,
            hasRestoredForCurrentNavigation
          });
        };

        const restoreScrollWithRetry = (targetY: number, reason: string, onSettled?: () => void) => {
          clearRestoreTimer();

          const positiveTargetY = Math.max(0, targetY);
          if (positiveTargetY === 0) {
            viewportScroller.scrollToPosition([0, 0]);
            onSettled?.();
            return;
          }

          let retries = 0;

          const restoreAttempt = () => {
            retries += 1;
            viewportScroller.scrollToPosition([0, positiveTargetY]);

            const currentY = window.scrollY;
            const maxScrollableY = getMaxScrollableY();
            const reachedTarget = Math.abs(currentY - positiveTargetY) <= 2;
            const canReachTarget = maxScrollableY >= positiveTargetY - 2;
            const shouldRetry = !reachedTarget && (!canReachTarget || retries < SCROLL_RESTORE_MAX_RETRIES);

            logger.log('restoreScrollWithRetry', `${SCROLL_DEBUG_PREFIX} Restore attempt`, {
              reason,
              retries,
              targetY: positiveTargetY,
              currentY,
              maxScrollableY,
              reachedTarget,
              canReachTarget
            });

            if (shouldRetry && retries < SCROLL_RESTORE_MAX_RETRIES) {
              restoreTimer = setTimeout(restoreAttempt, SCROLL_RESTORE_RETRY_DELAY_MS);
              return;
            }

            restoreTimer = null;
            onSettled?.();
          };

          restoreAttempt();
        };

        if (isPlatformServer(platformId)) {
          return Promise.resolve();
        }

        // ✅ Detect Query Param Changes
        router.events
          .pipe(
            filter((event): event is NavigationStart => event instanceof NavigationStart),
            pairwise() // Get previous & current navigation events
          )
          .subscribe(([prev, curr]) => {
            clearRestoreTimer();
            hasRestoredForCurrentNavigation = false;

            const prevUrl = getPath(prev.url); // Remove query params
            const currUrl = getPath(curr.url);
            const prevPage = getPageParam(prev.url);
            const currPage = getPageParam(curr.url);
            const isDetailToCatalogBack =
              curr.navigationTrigger === 'popstate' && /\/movie(\/|$)/.test(prevUrl) && /\/movies(\/|$)/.test(currUrl);
            const isCatalogToDetailForward =
              curr.navigationTrigger === 'imperative' &&
              /\/movies(\/|$)/.test(prevUrl) &&
              /\/movie(\/|$)/.test(currUrl);
            const isPopstatePageCleanup =
              prev.navigationTrigger === 'popstate' && curr.navigationTrigger === 'imperative';

            logger.log('navigationStartPair', `${SCROLL_DEBUG_PREFIX} NavigationStart pair`, {
              from: prev.url,
              to: curr.url,
              fromPage: prevPage,
              toPage: currPage,
              fromTrigger: prev.navigationTrigger,
              toTrigger: curr.navigationTrigger,
              currentScrollY: window.scrollY
            });

            if (isDetailToCatalogBack) {
              shouldRestoreCatalogBackScroll = true;
              logger.log('navigationStartPair', `${SCROLL_DEBUG_PREFIX} Detail -> catalog back navigation detected`, {
                from: prev.url,
                to: curr.url,
                toPage: currPage,
                restoredState: prev.restoredState ?? null,
                lastScrollPosition,
                shouldRestoreCatalogBackScroll
              });
            }

            if (isCatalogToDetailForward) {
              lastScrollPosition = window.scrollY;
              logger.log(
                'navigationStartPair',
                `${SCROLL_DEBUG_PREFIX} Catalog -> detail navigation detected, capturing scroll snapshot`,
                {
                  from: prev.url,
                  to: curr.url,
                  capturedScrollY: lastScrollPosition
                }
              );
            }

            if (prevUrl === currUrl) {
              if (isPopstatePageCleanup && window.scrollY === 0) {
                logger.log(
                  'navigationStartPair',
                  `${SCROLL_DEBUG_PREFIX} Skipping scroll snapshot for popstate cleanup navigation`,
                  {
                    from: prev.url,
                    to: curr.url,
                    fromPage: prevPage,
                    toPage: currPage,
                    currentScrollY: window.scrollY,
                    preservedLastScrollPosition: lastScrollPosition
                  }
                );
                return;
              }

              logger.log(
                'navigationStartPair',
                `${SCROLL_DEBUG_PREFIX} Query param update detected, preventing scroll reset`,
                {
                  url: currUrl,
                  fromPage: prevPage,
                  toPage: currPage,
                  capturedScrollY: window.scrollY
                }
              );
              lastScrollPosition = window.scrollY;
            }

            lastNavigationUrl = curr.url;
          });

        // ✅ Handle Scroll Restoration
        router.events.pipe(filter((event): event is Scroll => event instanceof Scroll)).subscribe(event => {
          if (event.routerEvent instanceof NavigationSkipped) {
            logger.log('handleScrollEvent', `${SCROLL_DEBUG_PREFIX} Ignoring Scroll event from NavigationSkipped`, {
              routerUrl: event.routerEvent.url
            });
            return;
          }

          const currentPage = getPageParam(event.routerEvent.url);
          logger.log('handleScrollEvent', `${SCROLL_DEBUG_PREFIX} Scroll event received`, {
            routerUrl: event.routerEvent.url,
            routerPage: currentPage,
            lastNavigationUrl,
            eventPosition: event.position ?? null,
            eventAnchor: event.anchor ?? null,
            lastScrollPosition,
            currentScrollY: window.scrollY
          });

          if (hasRestoredForCurrentNavigation) {
            logger.log(
              'handleScrollEvent',
              `${SCROLL_DEBUG_PREFIX} Skipping scroll restore because this navigation is already handled`,
              {
                routerUrl: event.routerEvent.url,
                routerPage: currentPage,
                lastScrollPosition,
                shouldRestoreCatalogBackScroll
              }
            );
            return;
          }

          if (event.position) {
            // ✅ Restore previous scroll position for normal navigation
            logger.log('handleScrollEvent', `${SCROLL_DEBUG_PREFIX} Restoring saved position`, {
              position: event.position
            });
            restoreScrollWithRetry(event.position[1], 'event.position', () => clearRestorationState('event.position'));
          } else if (event.anchor) {
            // ✅ Scroll to anchor if available
            clearRestoreTimer();
            logger.log('handleScrollEvent', `${SCROLL_DEBUG_PREFIX} Scrolling to anchor`, { anchor: event.anchor });
            viewportScroller.scrollToAnchor(event.anchor);
            clearRestorationState('event.anchor');
          } else {
            // This line will exclude scroll to top if pagination is applied, eg: load more button
            const isPaginationEnabled = event.routerEvent.url.includes('page=');
            const shouldRestoreFromCatalogBack =
              shouldRestoreCatalogBackScroll && /\/movies(\/|$)/.test(getPath(event.routerEvent.url));
            const targetY = isPaginationEnabled || shouldRestoreFromCatalogBack ? lastScrollPosition : 0;

            logger.log('handleScrollEvent', `${SCROLL_DEBUG_PREFIX} Default scroll handling`, {
              isPaginationEnabled,
              shouldRestoreFromCatalogBack,
              targetY,
              page: currentPage
            });

            // ✅ Default to scrolling to top if no anchor
            restoreScrollWithRetry(targetY, 'default', () => clearRestorationState('default'));
          }
        });

        return Promise.resolve();
      },
      multi: true,
      deps: [Router, ViewportScroller, PLATFORM_ID, LogService]
    }
  ]);
}
